import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User as OidcUser } from 'oidc-client-ts';
import { jwtDecode } from 'jwt-decode';
import { testMgr } from '../features/auth/utils/oidcConfig';

export type UserRole = 'admin' | 'editor' | 'viewer';

export interface User {
  id: string;
  username: string;
  email?: string;
  avatar?: string;
  role: UserRole;
  permissions: string[];
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isChatGPTAPI: boolean;

  login: (username: string, password: string) => boolean;
  loginWithOidc: (oidcUser: OidcUser) => void;
  logout: () => void;
  setUser: (user: User) => void;
  setToken: (token: string | null) => void;
  setIsChatGPTAPI: (value: boolean) => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  /**
   * 🆕 判断用户名是否包含 "vinka"（不区分大小写）
   * 用于控制工作流和运行日志的可见性
   */
  isMossUser: () => boolean;
}

// 从OIDC用户数据中解析出我们系统需要的用户信息
const mapOidcUserToAppUser = (oidcUser: OidcUser): User => {
  // 从OIDC profile声明中获取用户信息
  const profile = oidcUser.profile;

  // 假设profile中包含role和permissions信息
  // 实际字段名称根据OIDC提供者的配置可能不同
  const roleFromProfile = Array.isArray(profile.role)
    ? profile.role[0]
    : (profile.role || 'viewer');

  // 确保角色是有效的UserRole类型
  const role = (['admin', 'editor', 'viewer'].includes(roleFromProfile)
    ? roleFromProfile
    : 'viewer') as UserRole;

  // 解析权限
  const permissions = profile.permissions
    ? Array.isArray(profile.permissions)
      ? profile.permissions
      : [profile.permissions]
    : getDefaultPermissions(role);

  const tokenObj = localStorage.getItem('tokenObj');
  const user = tokenObj ? JSON.parse(tokenObj).loginIn : null;

  return {
    id: profile.sub,
    username: profile.preferred_username || profile.name || user || 'user',
    email: profile.email,
    role,
    permissions
  };
};

/**
 * 🆕 乐观鉴权：直接从 JWT 字符串中解析出用户信息
 * 用于首屏瞬间恢复 UI，无需等待 OIDC 元数据下载
 */
const mapJwtToUser = (token: string): User | null => {
  try {
    const profile: any = jwtDecode(token);

    // 基础角色映射逻辑（保持与 mapOidcUserToAppUser 一致）
    const roleFromProfile = Array.isArray(profile.role) ? profile.role[0] : (profile.role || 'viewer');
    const role = (['admin', 'editor', 'viewer'].includes(roleFromProfile) ? roleFromProfile : 'viewer') as UserRole;

    return {
      id: profile.sub || 'token-user',
      username: profile.preferred_username || profile.name || profile.unique_name || 'User',
      email: profile.email,
      role,
      permissions: profile.permissions || getDefaultPermissions(role)
    };
  } catch (e) {
    console.warn('[authStore] JWT 解析失败:', e);
    return null;
  }
};

// 根据角色获取默认权限
const getDefaultPermissions = (role: UserRole): string[] => {
  switch (role) {
    case 'admin':
      return ['workflow:create', 'workflow:edit', 'workflow:delete', 'workflow:view', 'runlog:view'];
    case 'editor':
      return ['workflow:create', 'workflow:edit', 'workflow:view', 'runlog:view'];
    case 'viewer':
    default:
      return ['workflow:view', 'runlog:view'];
  }
};

// Mock users for initial development without backend
const MOCK_USERS = [
  {
    id: '0',
    username: 'moss_apple',
    password: '123456789',
    email: 'moss_apple@example.com',
    role: 'admin' as UserRole,
    permissions: ['workflow:create', 'workflow:edit', 'workflow:delete', 'workflow:view', 'runlog:view']
  },
  {
    id: '1',
    username: 'admin',
    password: 'admin123',
    email: 'admin@example.com',
    role: 'admin' as UserRole,
    permissions: ['workflow:create', 'workflow:edit', 'workflow:delete', 'workflow:view', 'runlog:view']
  },
  {
    id: '2',
    username: 'editor',
    password: 'editor123',
    email: 'editor@example.com',
    role: 'editor' as UserRole,
    permissions: ['workflow:create', 'workflow:edit', 'workflow:view', 'runlog:view']
  },
  {
    id: '3',
    username: 'viewer',
    password: 'viewer123',
    email: 'viewer@example.com',
    role: 'viewer' as UserRole,
    permissions: ['workflow:view', 'runlog:view']
  }
];

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      token: null,
      isChatGPTAPI: false,

      login: (username, password) => {
        const user = MOCK_USERS.find(u => u.username === username && u.password === password);

        if (user) {
          const { password: _, ...userWithoutPassword } = user;
          set({
            isAuthenticated: true,
            user: userWithoutPassword,
            token: `mock-token-${user.id}-${Date.now()}`
          });
          return true;
        }

        return false;
      },

      loginWithOidc: (oidcUser) => {
        const user = mapOidcUserToAppUser(oidcUser);
        set({
          isAuthenticated: true,
          user,
          token: oidcUser.access_token
        });
      },

      logout: () => {
        // 首先清除本地状态
        set({
          isAuthenticated: false,
          user: null,
          token: null
        });

        // 检查是否跳过 SSO（开发环境）
        const skipSso = import.meta.env.VITE_SKIP_SSO === 'true';

        if (skipSso) {
          // 开发环境：只清除 localStorage，然后跳转到欢迎页（用户可以从那里选择登录）

          localStorage.clear();
          window.location.href = '/welcome';
          return;
        }

        // 生产环境：正常的 SSO 退出流程
        const identityServerBase = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') || 'https://gate.shjinjia.com.cn'
        const client_id = import.meta.env.VITE_OIDC_CLIENT_ID || 'moss_prod'
        const name = `oidc.user:${identityServerBase}:${client_id}`

        const token = localStorage.getItem('access_token')

        if (token) {

          Object.keys(localStorage).forEach(key => {
            // 如果键不是需要保留的键，则删除它
            if (key !== name) {

              localStorage.removeItem(key);
            }
          });
          testMgr.signoutRedirect()
        }
      },

      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setIsChatGPTAPI: (value) => set({ isChatGPTAPI: value }),

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        return user.permissions.includes(permission);
      },

      hasRole: (role) => {
        const { user } = get();
        if (!user) return false;

        if (Array.isArray(role)) {
          return role.includes(user.role);
        }

        return user.role === role;
      },

      isMossUser: () => {
        const { user } = get();
        if (!user) return false;
        // 将用户名转小写，并将连字符 - 和下划线 _ 统一为空字符后匹配 moss
        const normalizedUsername = user.username.toLowerCase().replace(/[-_]/g, '');
        return normalizedUsername.includes('moss');
      }
    }),
    {
      name: 'auth-storage',
      // ✅ P1-2 修复：常规持久化不包含 Token
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        isChatGPTAPI: state.isChatGPTAPI
      })
    }
  )
);

/**
 * 🆕 执行“乐观认证恢复”
 * 在 main.tsx 入口处调用，实现首帧瞬间渲染
 */
export const performOptimisticRestore = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return;

  const authStore = useAuthStore.getState();

  // 如果状态里已经有了，不再重复解析
  if (authStore.isAuthenticated && authStore.user) return;

  const user = mapJwtToUser(token);
  if (user) {
    console.log('[authStore] ⚡ 执行乐观身份恢复:', user.username);
    useAuthStore.setState({
      isAuthenticated: true,
      user,
      token
    });
  }
};